import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { api, formatApiError, PLATFORM_META } from "../api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Compass, Copy, MousePointerClick, CheckCircle2, TrendingUp, Plus, ExternalLink } from "lucide-react";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import CreditsPanel from "../components/CreditsPanel";

function Kpi({ icon: Icon, label, value, tone = "primary", testid }) {
  const bg = tone === "accent" ? "bg-[#E57C4F]/10 text-[#E57C4F]" : tone === "green" ? "bg-[#10B981]/10 text-[#10B981]" : "bg-[#0A2540]/10 text-[#0A2540]";
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 hover:-translate-y-0.5 transition-transform duration-200" data-testid={testid}>
      <div className={`w-10 h-10 rounded-lg grid place-items-center ${bg}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="mt-4 text-3xl font-display font-bold text-[#0A2540]">{value}</div>
      <div className="text-sm text-slate-500 mt-1">{label}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [tours, setTours] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [tr, s] = await Promise.all([api.get("/tours"), api.get("/analytics/summary")]);
      setTours(tr.data);
      setSummary(s.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const copyLink = (tourId) => {
    const link = `${window.location.origin}/r/${tourId}`;
    navigator.clipboard.writeText(link);
    toast.success(t("toast_link_customer_copied"));
  };

  const platformData = summary
    ? Object.keys(PLATFORM_META).map((k) => ({
        name: PLATFORM_META[k].label,
        clicks: summary.platform_counts?.[k] || 0,
      }))
    : [];

  return (
    <DashboardLayout>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-sm text-slate-500">{t("dashboard_hi")} {user?.business_name}</div>
          <h1 className="font-display text-3xl font-bold text-[#0A2540] tracking-tight">{t("dashboard_title")}</h1>
        </div>
        <Button
          onClick={() => navigate("/dashboard/tours/new")}
          data-testid="create-tour-btn"
          className="rounded-md bg-[#0A2540] hover:bg-[#0A2540]/90 text-white gap-2"
        >
          <Plus className="w-4 h-4" /> {t("new_tour_btn")}
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <Kpi icon={Compass} label={t("kpi_tours")} value={summary?.total_tours ?? "—"} testid="kpi-tours" />
        <Kpi icon={MousePointerClick} label={t("kpi_clicks")} value={summary?.total_clicks ?? "—"} tone="accent" testid="kpi-clicks" />
        <Kpi icon={CheckCircle2} label={t("kpi_completions")} value={summary?.total_completions ?? "—"} tone="green" testid="kpi-completions" />
        <Kpi icon={TrendingUp} label={t("kpi_conversion")} value={summary ? `${summary.conversion_rate}%` : "—"} testid="kpi-conversion" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-8">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-6" data-testid="chart-platform-clicks">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display font-semibold text-lg text-[#0A2540]">{t("chart_platforms_title")}</h2>
            <span className="text-xs text-slate-500">{t("chart_platforms_sub")}</span>
          </div>
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={platformData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#5C6B7A" }} />
                <YAxis tick={{ fontSize: 12, fill: "#5C6B7A" }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="clicks" fill="#2B8C9F" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <CreditsPanel />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-3 bg-white border border-slate-200 rounded-xl p-6" data-testid="recent-tours-card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display font-semibold text-lg text-[#0A2540]">{t("your_tours")}</h2>
            <Link to="/dashboard/tours" className="text-xs font-semibold text-[#2B8C9F] hover:underline" data-testid="view-all-tours-link">
              {t("view_all")}
            </Link>
          </div>
          {loading ? (
            <div className="text-sm text-slate-500">{t("loading")}</div>
          ) : tours.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center">
              {t("no_tours_yet")}
              <div className="mt-4">
                <Button onClick={() => navigate("/dashboard/tours/new")} className="rounded-md bg-[#E57C4F] hover:bg-[#E57C4F]/90 text-white" data-testid="create-first-tour-btn">
                  {t("create_first")}
                </Button>
              </div>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {tours.slice(0, 5).map((tr) => (
                <li key={tr.id} className="py-3 flex items-center justify-between" data-testid={`tour-row-${tr.id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-800 truncate">{tr.name}</div>
                    <div className="text-xs text-slate-500">{tr.clicks} {t("events_opens")} · {tr.completions} {t("events_completed")}</div>
                  </div>
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="ghost" onClick={() => copyLink(tr.id)} data-testid={`copy-link-${tr.id}`}>
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" asChild data-testid={`open-tour-${tr.id}`}>
                      <a href={`/r/${tr.id}`} target="_blank" rel="noreferrer">
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
